import React from 'react';
import {Select} from '@mantine/core';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {ItemAssignmentStateEnum} from 'src/api/ProactApiClient';
import {SelectableCard} from 'src/design/molecules/SelectableCard';
import {useGetItemDefinitionSelectOptionsQuery} from 'src/redux/queries/itemDefinition';
import {mapEnumToSelectOption} from 'src/utilities/enumUtils';
import type {TriggerEditorProps} from './TriggerCard';
import {SectionLabel} from './SectionLabel';

const stateConfigs = mapEnumToSelectOption(ItemAssignmentStateEnum)
  .filter(o => o.id !== ItemAssignmentStateEnum.Invalid)
  .map(o => ({
    type: o.id as ItemAssignmentStateEnum,
    label: String(o.label).splitUppercase(),
    description: `Trigger when item assignment state changes to ${String(o.label).splitUppercase()}.`,
  }));

export const TriggerItemTrackerEditor: React.FC<TriggerEditorProps> = ({
  trigger,
  onTriggerChange,
}) => {
  const {data, isLoading} = useGetItemDefinitionSelectOptionsQuery({
    start: 0,
    length: 100,
  });

  const itemOptions =
    data?.data?.map(item => ({
      value: String(item.id),
      label: item.label ?? String(item.id),
    })) ?? [];

  const handleItemChange = (value: string | null) => {
    onTriggerChange({
      ...trigger,
      triggerItemDefinitionId: value ? Number(value) : undefined,
    });
  };

  const handleStateChange = (value: string) => {
    onTriggerChange({
      ...trigger,
      triggerItemAssignmentState: Number(value) as ItemAssignmentStateEnum,
    });
  };

  return (
    <Stack gap="md">
      <Select
        label="Item definition"
        description="Select the item type that will trigger this workflow."
        data={itemOptions}
        value={
          trigger.triggerItemDefinitionId
            ? String(trigger.triggerItemDefinitionId)
            : null
        }
        onChange={handleItemChange}
        disabled={isLoading}
        searchable
      />

      <SectionLabel label="Assignment State" />
      <SimpleGrid cols={{base: 1, sm: 2, md: 3}}>
        {stateConfigs.map(c => (
          <SelectableCard
            key={c.type}
            label={c.label}
            description={c.description}
            value={String(c.type)}
            selected={trigger.triggerItemAssignmentState === c.type}
            onClick={handleStateChange}
          />
        ))}
      </SimpleGrid>
    </Stack>
  );
};
